import { useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { TextField, useMediaQuery } from '@mui/material';
import { breakpoints } from '../../constants';
import { TFormValues } from '../../types';
import { Row } from '../Row';

interface IProps {
    formValues: TFormValues;
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    setFieldValue(field: keyof TFormValues, value: string): void;
}

interface IPostcodeRequest {
    zipCode: string;
    houseNumber: string;
}

interface IPostcodeResponse {
    city: string;
    street: string;
}

export const AddressSection = ({
    formValues,
    getFieldError,
    setFieldValue,
}: IProps) => {
    const [zipcodeError, setZipcodeError] = useState(false);
    const mediumBreakpoint = useMediaQuery(`(${breakpoints.mediumMin})`);

    const handleHouseNumberBlur = () => {
        if (
            !process.env.REACT_APP_POSTCODE_API_URL ||
            !formValues.zipcode ||
            !formValues.houseNumber
        )
            return;

        axios
            .post<IPostcodeRequest, AxiosResponse<IPostcodeResponse>>(
                process.env.REACT_APP_POSTCODE_API_URL,
                {
                    zipCode: formValues.zipcode,
                    houseNumber: formValues.houseNumber,
                }
            )
            .then((res) => {
                const { street, city } = res.data;

                setFieldValue('street', street);
                setFieldValue('city', city);
            });
    };

    const handleZipcodeBlur = () => {
        if (formValues.zipcode === '') return setZipcodeError(false);

        const formattedZipcode = formValues.zipcode
            .replace(/\s/g, '')
            .toUpperCase();

        setFieldValue('zipcode', formattedZipcode);

        // 1234AB
        const zipcodeRegex = /^\d{4}[A-Z]{2}$/;

        if (!formattedZipcode.match(zipcodeRegex)) {
            setZipcodeError(true);

            return;
        }

        setZipcodeError(false);
    };

    const getZipcodeHelperText = () => {
        if (getFieldError('zipcode')) return getFieldError('zipcode');
        if (zipcodeError) return 'Geen geldige postcode';

        return '';
    };

    return (
        <>
            <Row>
                <TextField
                    error={!!getFieldError('zipcode') || zipcodeError}
                    helperText={getZipcodeHelperText()}
                    id={formValues.zipcode}
                    label="Postcode *"
                    placeholder="1234AB"
                    value={formValues.zipcode}
                    onBlur={handleZipcodeBlur}
                    onChange={(e) => setFieldValue('zipcode', e.target.value)}
                />
                <TextField
                    error={!!getFieldError('houseNumber')}
                    helperText={getFieldError('houseNumber')}
                    id={formValues.houseNumber}
                    label="Huisnummer *"
                    sx={{ margin: `${mediumBreakpoint && '0 10px'}` }}
                    value={formValues.houseNumber}
                    onBlur={handleHouseNumberBlur}
                    onChange={(e) =>
                        setFieldValue('houseNumber', e.target.value)
                    }
                />
                <TextField
                    error={!!getFieldError('houseNumberAddition')}
                    helperText={getFieldError('houseNumberAddition')}
                    id={formValues.houseNumberAddition}
                    label="Toevoeging"
                    value={formValues.houseNumberAddition}
                    onChange={(e) =>
                        setFieldValue('houseNumberAddition', e.target.value)
                    }
                />
            </Row>
            <TextField
                error={!!getFieldError('street')}
                helperText={getFieldError('street')}
                id={formValues.street}
                label="Straat *"
                value={formValues.street}
                onChange={(e) => setFieldValue('street', e.target.value)}
            />
            <TextField
                error={!!getFieldError('city')}
                helperText={getFieldError('city')}
                id={formValues.city}
                label="Plaats *"
                value={formValues.city}
                onChange={(e) => setFieldValue('city', e.target.value)}
            />
        </>
    );
};
